import {
  type SessionRecapCitation,
  type SessionRecapHighlight,
} from '@web-app/server/api/types';

export type FormAnswer = {
  label: string;
  answer: string;
};

export type ChartNoteData = {
  chartingItemId: string;
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  interventions?: string;
};

/**
 * Extracts SOAP note fields from form answers.
 * Returns null if all SOAP fields are empty.
 */
export function extractSoapNotesFromFormAnswers(
  formGroupId: string,
  formAnswers: FormAnswer[],
): ChartNoteData | null {
  const answersByLabel = Object.fromEntries(
    formAnswers.map(({ label, answer }) => [label.toLowerCase(), answer]),
  );

  const { subjective, objective, assessment, plan, interventions } =
    answersByLabel;

  // Return null if all SOAP note fields are empty
  const hasContent = [
    subjective,
    objective,
    assessment,
    plan,
    interventions,
  ].some((value) => value?.trim());

  if (!hasContent) return null;

  return {
    chartingItemId: formGroupId,
    subjective,
    objective,
    assessment,
    plan,
    interventions,
  };
}

/**
 * Maps citation types to their corresponding URLs.
 * Returns null if the citation type is not supported or required data is missing.
 */
export function buildCitationUrl(
  citation: SessionRecapCitation,
  clientId: string | number | null | undefined,
): string | null {
  if (!clientId) return null;

  switch (citation.type) {
    case 'healthie_charting_note':
      if (!citation.source_id) return null;
      return `${process.env.NEXT_PUBLIC_HEALTHIE_URL}/users/${clientId}/private_notes/view/${citation.source_id}`;
    default:
      return null;
  }
}

/**
 * Formats AI-generated highlight strings into SessionRecapHighlight objects with citations.
 */
export function formatHighlightsWithCitations(
  highlights: string[],
  chartingItemId: string,
): SessionRecapHighlight[] {
  return highlights.map((content) => ({
    content,
    citations: [
      {
        type: 'healthie_charting_note',
        source_id: chartingItemId,
      },
    ],
  }));
}
